import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { ChevronDown, Info } from 'lucide-react';
import { BottomSheet, Button, DropdownMenu, Popover } from '@/components/ui';
import dollarIcon from '@/assets/dollar-icon.svg';
import sheetIcon from '@/assets/sheet-icon.svg';
import { cn } from '@/lib/utils';
import type { OrderSide, BuyFormat, TradeSurface } from '@/analytics';
import {
  usePlaeeOrderBookGetOrderBook,
  usePredictionMarketConfigResolvePriceCube,
  useTradingOrderPlaceOrder,
} from '@/lib/endpoints';
import { invalidateTradingCaches } from '@/lib/trading/invalidate-trading-caches';
import {
  PlaceOrderDtoAction,
  PlaceOrderDtoSide,
  PlaceOrderDtoType,
  PredictionMarketConfigResolvePriceCubeFormat,
  type MarketOptionDto,
} from '@/lib/models';
import { centsToPercent, toCents } from '@/lib/binary-market';
import { useTerritory } from '@/lib/territory/useTerritory';
import { useBuyInFooter } from '@/lib/footer/useBuyInFooter';
import { useOpenTerritoryGate } from '@/store/territoryGateStore';
import { useOnboardingGate } from '@/lib/auth/useOnboardingGate';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import { ORDER_FAILURE_MESSAGE, toOrderFailureReason } from '@/lib/trading/order-failure';
import { computeBuyQuote } from './compute-buy-quote';
import { BuyKeypad } from './BuyKeypad';
import { MarketOutcomePricesSubscriber, optionCents, targetCents, useOutcomePrices } from './useOutcomePrices';

/** The option the popup trades (a backend-resolved option + the event name for the title). */
export type BuyMarketContext = MarketOptionDto & { eventName: string };

export interface BuyInPopupProps {
  /** The option to buy into, or `null` when the popup is closed. */
  market: BuyMarketContext | null;
  /** Side the popup opens on (§6.1 — toggleable inside). */
  initialSide: OrderSide;
  /** Where the popup was opened from — analytics surface attribution. */
  surface: TradeSurface;
  /** Close the popup. */
  onClose: () => void;
}

const DOLLARS = PredictionMarketConfigResolvePriceCubeFormat.DOLLARS;
const CONTRACTS = PredictionMarketConfigResolvePriceCubeFormat.CONTRACTS;

/**
 * Group the integer part of a live-typed amount with thousands separators while
 * preserving the fractional part exactly as typed (including a trailing `.` or
 * trailing zeros) so the big amount display reads e.g. `1,000.50` mid-entry.
 */
function groupAmountStr(amountStr: string): string {
  const s = amountStr === '' ? '0' : amountStr;
  const [intPart, fracPart = ''] = s.split('.');
  const grouped = intPart === '' ? '' : Number(intPart).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return s.includes('.') ? `${grouped}.${fracPart}` : grouped;
}

/**
 * Buy-in popup (ARC-82 §6).
 *
 * Answer toggle (§6.1), Dollars/Contracts toggle (§6.2), admin quick-add cubes,
 * amount entry, live odds + "To Win" + price breakdown (§6.3). The chosen side
 * maps to the option's `yes`/`no` buy target (for a 2-outcome binary these are
 * distinct outcomes), and the order places on that target via
 * {@link useTradingOrderPlaceOrder}, then refetches the order + position mirrors.
 */
export function BuyInPopup({ market, initialSide, onClose }: BuyInPopupProps) {
  const [side, setSide] = useState<OrderSide>(initialSide);
  const [format, setFormat] = useState<BuyFormat>('dollars');
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);
  const [amountStr, setAmountStr] = useState('');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { prices, setPrice, seedPrice } = useOutcomePrices();
  const queryClient = useQueryClient();
  const territory = useTerritory();
  const { data: buyInFooter } = useBuyInFooter(territory?.country?.code);
  const footerHtml = buyInFooter?.contentHtml ?? '';
  const footerClean = footerHtml ? DOMPurify.sanitize(footerHtml, { ADD_ATTR: ['target', 'rel'] }) : '';
  const openTerritoryGate = useOpenTerritoryGate();
  const ensureOrderAuth = useOnboardingGate('order_gate');
  const tradingEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_ORDER_PLACEMENT);
  const placeOrder = useTradingOrderPlaceOrder();

  // Reset entry whenever a new option opens the popup (switching candidate counts).
  const [openedId, setOpenedId] = useState<string | null>(null);
  if (market && market.id !== openedId) {
    setOpenedId(market.id);
    setSide(initialSide);
    setFormat('dollars');
    setFormatMenuOpen(false);
    setAmountStr('');
    setShowBreakdown(false);
    setError(null);
  }

  const dollarCubes = usePredictionMarketConfigResolvePriceCube(
    { format: DOLLARS },
    { query: { enabled: Boolean(market) } },
  );
  const contractCubes = usePredictionMarketConfigResolvePriceCube(
    { format: CONTRACTS },
    { query: { enabled: Boolean(market) } },
  );

  // Price safety net for quiet markets: an outcome with no API seed and no
  // Centrifugo tick yet would show "—" AND dead-lock the Buy button (canBuy
  // needs a price). The order-book snapshot carries the outcome's current
  // yes/no prices, so fetch it for the active side's target — but only while a
  // price is actually missing, and seed-if-absent so a snapshot never
  // overrides a tick that already arrived.
  const seedTarget = market ? (side === 'yes' ? market.yes : market.no) : null;
  const seedOutcomeId = seedTarget?.externalOutcomeId;
  const needsPriceSeed = seedTarget != null && targetCents(seedTarget, prices) == null;
  const { data: bookSnapshot } = usePlaeeOrderBookGetOrderBook(seedOutcomeId ?? '', {
    // Same freshness rationale as OutcomeOrderBook: the global 60s staleTime is
    // too old for a price the user is about to trade on.
    query: { enabled: needsPriceSeed, staleTime: 5_000 },
  });
  useEffect(() => {
    if (!seedOutcomeId || !bookSnapshot) return;
    // Snapshot prices are dollars in [0, 1]; the overlay map holds cents.
    const yesCents = toCents(bookSnapshot.yesPrice);
    const noCents = toCents(bookSnapshot.noPrice) ?? (yesCents != null ? 100 - yesCents : null);
    if (yesCents == null || noCents == null) return;
    seedPrice(seedOutcomeId, { yesCents, noCents });
  }, [bookSnapshot, seedOutcomeId, seedPrice]);

  const open = Boolean(market);
  if (!market) {
    return <BottomSheet open={false} onClose={onClose} />;
  }
  const activeMarket = market;

  // Live-or-seed cents for each side; the chosen side feeds the quote + order.
  const cents = optionCents(activeMarket, prices);
  const sideCents = side === 'yes' ? cents.yes : cents.no;
  const target = side === 'yes' ? activeMarket.yes : activeMarket.no;

  const amount = parseFloat(amountStr) || 0;
  const quote = computeBuyQuote(amount, format, sideCents);
  const cubes = (format === 'dollars' ? dollarCubes.data?.values : contractCubes.data?.values) ?? [];

  function handleKey(key: string) {
    setAmountStr((prev) => {
      if (key === '.' && prev.includes('.')) return prev;
      if (key === '.' && prev === '') return '0.';
      if (prev === '0' && key !== '.') return key;
      return prev + key;
    });
  }

  function handleCube(value: number) {
    setAmountStr((prev) => {
      const next = (parseFloat(prev) || 0) + value;
      return format === 'dollars' ? String(next) : String(Math.round(next));
    });
  }

  function handleToggleSide(next: OrderSide) {
    if (next === side) return;
    setSide(next);
  }

  function handleSelectFormat(next: BuyFormat) {
    if (next === format) return;
    setFormat(next);
    setAmountStr('');
  }

  const groupedDisplay = groupAmountStr(amountStr);
  const amountDisplay = format === 'dollars' ? `$${groupedDisplay}` : groupedDisplay;
  const cubePrefix = format === 'dollars' ? '+$' : '+';
  const oddsPercent = centsToPercent(sideCents) ?? 0;
  const hasAmount = amount > 0;
  const sizeOk = format === 'dollars' ? amount > 0 : Math.round(amount) >= 1;
  const canBuy = tradingEnabled && sizeOk && sideCents != null && !placeOrder.isPending;
  const sideLabel = side === 'yes' ? 'Yes' : 'No';
  const groupedAmount = amount.toLocaleString('en-US', { maximumFractionDigits: 2 });
  const buyLabel = placeOrder.isPending
    ? 'Placing…'
    : hasAmount
      ? `Buy ${format === 'dollars' ? `$${groupedAmount}` : groupedAmount} ${sideLabel}`
      : `Buy ${sideLabel}`;

  function handleBuy() {
    if (!canBuy || sideCents == null) return;
    // Signup-funnel resume comes before any verification gate (ARC-145).
    if (!ensureOrderAuth()) return;
    if (territory?.blocked) {
      openTerritoryGate({ reason: territory.reason });
      return;
    }
    const outcomeId = target.externalOutcomeId;
    setError(null);
    placeOrder.mutate(
      {
        // "Buy now" = market order (type=market + amount or count, no price);
        // omitting `type` would rest a limit at sideCents. sideCents is kept
        // only for the odds + payout display.
        data: {
          outcomeId,
          side: target.side === 'yes' ? PlaceOrderDtoSide.yes : PlaceOrderDtoSide.no,
          action: PlaceOrderDtoAction.buy,
          type: PlaceOrderDtoType.market,
          ...(format === 'dollars' ? { amount } : { count: Math.round(amount) }),
        },
      },
      {
        onSuccess: (result) => {
          if (!result.success) {
            const reason = toOrderFailureReason(result.errorCode);
            setError(ORDER_FAILURE_MESSAGE[reason]);
            return;
          }
          invalidateTradingCaches(queryClient);
          onClose();
        },
        onError: () => {
          setError(ORDER_FAILURE_MESSAGE.network);
        },
      },
    );
  }

  return (
    <BottomSheet open={open} onClose={onClose}>
      <div className="w-full bg-surface max-w-[450px] mx-auto">
        <MarketOutcomePricesSubscriber
          marketId={activeMarket.marketId}
          externalMarketId={activeMarket.externalMarketId}
          onPrice={setPrice}
        />

        <p className="mb-1 text-center text-sm font-semibold text-primary">{activeMarket.label ?? activeMarket.eventName}</p>

        <div className="mx-auto w-fit">
          <DropdownMenu
            open={formatMenuOpen}
            onClose={() => setFormatMenuOpen(false)}
            value={format}
            onSelect={handleSelectFormat}
            align="start"
            items={[
              { value: 'dollars', label: 'Buy In Dollars', iconSrc: dollarIcon },
              { value: 'contracts', label: 'Buy In Contracts', iconSrc: sheetIcon },
            ]}
            anchor={
              <button
                type="button"
                onClick={() => setFormatMenuOpen((o) => !o)}
                className="flex items-center gap-1.5 text-accent font-semibold text-lg underline bg-transparent border-none cursor-pointer"
              >
                <img src={format === 'dollars' ? dollarIcon : sheetIcon} alt="" className="size-5" />
                <span>Buy In {format === 'dollars' ? 'Dollars' : 'Contracts'}</span>
                <ChevronDown className="size-4" />
              </button>
            }
          />
        </div>

        <div className="flex flex-col items-center mt-6 mb-4">
          <span className={cn('text-6xl font-bold', hasAmount ? 'text-accent-dark' : 'text-muted')}>{amountDisplay}</span>
          {hasAmount && (
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-lg font-semibold text-primary">Payout</span>
              <span className="text-lg font-semibold text-success">
                ${quote.toWinDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <Popover
                open={showBreakdown}
                onClose={() => setShowBreakdown(false)}
                align="start"
                className="left-[calc(50%-30px)] -translate-x-1/2 w-[248px] p-4 text-sm text-primary"
                anchor={
                  <button
                    type="button"
                    aria-label="Price breakdown"
                    onClick={() => setShowBreakdown((s) => !s)}
                    className="text-muted bg-transparent border-none cursor-pointer"
                  >
                    <Info className="size-4" />
                  </button>
                }
              >
                <p className="mb-2 font-medium capitalize">Price Breakdown</p>
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between">
                    <span>Price per contract</span>
                    <span className="tabular-nums">{sideCents ?? 0}¢</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Contracts</span>
                    <span className="tabular-nums">
                      {quote.contracts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total cost</span>
                    <span className="tabular-nums">
                      ${quote.costDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                <div className="my-2 border-t border-border" />
                <div className="flex justify-between font-medium">
                  <span>Payout</span>
                  <span className="tabular-nums text-success">
                    ${quote.toWinDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </Popover>
            </div>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2 mb-4">
          {cubes.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => handleCube(value)}
              className="h-10 rounded-pill border border-border text-primary font-semibold text-sm bg-surface hover:bg-surface-muted active:opacity-70 transition-colors"
            >
              {cubePrefix}
              {value}
            </button>
          ))}
        </div>

        <BuyKeypad onKey={handleKey} onBackspace={() => setAmountStr((p) => p.slice(0, -1))} />

        {/* Selected side = big gradient CTA (buys); other side = small outline pill (switches side). */}
        <div className="mt-5 mb-7 flex items-center gap-3">
          {side === 'yes' ? (
            <div className="relative flex flex-1 flex-col items-center">
              <Button
                variant="success"
                className="w-full h-[50px] rounded-pill text-[22px] whitespace-nowrap"
                disabled={!canBuy}
                onClick={handleBuy}
              >
                {buyLabel}
              </Button>
              <span className="absolute top-full mt-1 text-sm text-muted">
                Probability <span className="font-semibold">{oddsPercent}%</span>
              </span>
            </div>
          ) : (
            <Button
              variant="outline"
              size="md"
              className="w-[130px] shrink-0 h-[41px] text-base border-success text-success-dark"
              onClick={() => handleToggleSide('yes')}
            >
              Buy Yes
            </Button>
          )}

          {side === 'no' ? (
            <div className="relative flex flex-1 flex-col items-center">
              <Button
                variant="danger"
                className="w-full h-[50px] rounded-pill text-[22px] whitespace-nowrap"
                disabled={!canBuy}
                onClick={handleBuy}
              >
                {buyLabel}
              </Button>
              <span className="absolute top-full mt-1 text-sm text-muted">
                Probability <span className="font-semibold">{oddsPercent}%</span>
              </span>
            </div>
          ) : (
            <Button
              variant="outline"
              size="md"
              className="w-[130px] shrink-0 h-[41px] text-base border-danger-alt text-danger-alt"
              onClick={() => handleToggleSide('no')}
            >
              Buy No
            </Button>
          )}
        </div>

        {(error || !tradingEnabled) && (
          <p className={cn('text-center text-xs font-medium', error ? 'text-danger-alt' : 'text-muted')}>
            {error ?? 'Trading is temporarily paused'}
          </p>
        )}

        {footerClean && (
          <div
            className="mt-4 text-center text-[11px] leading-snug text-muted"
            dangerouslySetInnerHTML={{ __html: footerClean }}
          />
        )}
      </div>
    </BottomSheet>
  );
}
